import { ReactNode } from 'react';

import { ReactComponent as ExploreTradeoffsImage } from '@backrs_ui/grow-ui/public/images/explore_tradeoffs.svg';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type ExploreTradeoffsCardProps = Readonly<{
  title: string;
  description: string;
  inlineAction?: ReactNode;
  belowAction?: ReactNode;
}>;

export const ExploreTradeoffsCard = ({
  title,
  description,
  inlineAction,
  belowAction,
}: ExploreTradeoffsCardProps) => (
  <Box sx={containerSx}>
    <Box sx={imageSx}>
      <ExploreTradeoffsImage aria-hidden="true" preserveAspectRatio="xMidYMid slice" />
    </Box>
    <Box sx={contentSx}>
      <Typography
        variant="text-lg-semibold"
        color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
        sx={titleSx}
      >
        {title}
      </Typography>
      <Typography
        variant="text-sm-regular"
        color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        sx={descriptionSx(!!inlineAction)}
      >
        {description}
      </Typography>
      {inlineAction && <Box sx={inlineActionSx}>{inlineAction}</Box>}
      {belowAction && <Box sx={belowActionSx}>{belowAction}</Box>}
    </Box>
  </Box>
);

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  overflow: 'hidden',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
});

const imageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  height: '157px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: backrs.colors['Brand/500'],
  overflow: 'hidden',
  '& > svg': {
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'block',
  },
  '@container (min-width: 600px)': {
    width: '220px',
    height: 'auto',
    alignSelf: 'stretch',
    '& > svg': {
      width: '100%',
      height: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
    },
  },
});

const contentSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  minWidth: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'stretch',
  padding: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: `
      "title actions"
      "description description"
      "below below"
    `,
    columnGap: backrs.spacing['spacing-xl'],
    rowGap: 0,
    alignContent: 'center',
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-xxs'],
  '@container (min-width: 600px)': {
    gridArea: 'title',
    alignSelf: 'center',
    marginBottom: 0,
  },
});

const descriptionSx =
  (hasInlineAction: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    '@container (min-width: 600px)': {
      gridArea: 'description',
      marginTop: hasInlineAction ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-xxs'],
    },
  });

const inlineActionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: backrs.spacing['spacing-2xl'],
  '@container (min-width: 600px)': {
    gridArea: 'actions',
    alignSelf: 'center',
    marginTop: 0,
  },
});

const belowActionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: backrs.spacing['spacing-2xl'],
  '@container (min-width: 600px)': {
    gridArea: 'below',
    alignItems: 'flex-start',
  },
});
